import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './users-page.css';
import NavigationV2 from '../../components/v2/NavigationV2.jsx';
import Pagination from '../../components/v2/Pagination.jsx';
import UsersTable, { COLUMN_DEFS } from './components/UsersTable.jsx';
import UserGroupsTable, { GROUP_COLUMN_DEFS } from './components/UserGroupsTable.jsx';
import usePersistedColumns from '../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../hooks/usePersistedPerPage.js';
import UserDrawer from './components/UserDrawer.jsx';
import UserGroupDrawer from './components/UserGroupDrawer.jsx';
import ResetPasswordDialog from './components/ResetPasswordDialog.jsx';
import UserUploadDialog from './components/UserUploadDialog.jsx';
import ConfirmTypedDialog from '../../components/v2/ConfirmTypedDialog.jsx';
// Shared components from residents (fully generic)
import ActiveFilterBar from '../../components/v2/ActiveFilterBar.jsx';
import Toast from '../../components/v2/Toast.jsx';
import useToast from '../../hooks/useToast.js';
import ColumnVisibilityToggle from '../../components/v2/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../../components/v2/TableSkeleton.jsx';
// V2 data layer
import {
  getSession, setSession, getCampusId, getUserId, getSessionId,
  getCampusList, getRoleWeight, getCampusConfig, updateSessionCampus,
} from '../../services/session.js';
import {
  fetchUsers, fetchRoles, fetchUserGroupList,
  addUser, addUsers, editUser, deleteUsers, resetPassword,
  fetchSchedules, registerUserSip,
} from './api/usersApi.js';
import {
  fetchUserGroups, addUserGroup, editUserGroup, deleteUserGroups,
} from './api/userGroupsApi.js';
import { exportUsersCsv, exportUserGroupsCsv } from '../../services/csvExport.js';
import { iAmHigherAuthority as iAmHigherAuth } from '../../libs.jsx';
import { uploadInChunks, UPLOAD_CHUNK_SIZE } from './userCsvUpload.js';

/** All column keys per tab */
const ALL_USER_KEYS = COLUMN_DEFS.map((c) => c.key);
const ALL_GROUP_KEYS = GROUP_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns — show all by default (user can hide via column toggle) */
const DEFAULT_USER_VISIBLE = ALL_USER_KEYS;
const DEFAULT_GROUP_VISIBLE = ALL_GROUP_KEYS;

/**
 * UsersPage — main orchestrator for the /users V2 redesign.
 *
 * Manages: real API data loading for Users and User Groups tabs,
 * search, column filters, sort, pagination, row selection, drawers,
 * tab switching, and role-weight authorization.
 */
const UsersPage = ({ initialTab = 'users' }) => {
  // ---------- Session ----------
  const sessionCampusId = getCampusId();
  const campusList = useMemo(() => getCampusList() || [], []);
  const myRoleWeight = getRoleWeight();
  const myUsername = useMemo(() => getSession()?.username || '', []);
  const displayEditAllowed = useMemo(() => !!getSession()?.displayEdit, []);
  const isCorp = useMemo(() => getSession()?.corp === 1, []);

  // ---------- Data & Loading ----------
  const [usersData, setUsersData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [groupListData, setGroupListData] = useState([]);
  const [userGroupsData, setUserGroupsData] = useState([]);
  const [schedulesData, setSchedulesData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [campusId, setCampusId] = useState(sessionCampusId || 0);
  const isCloud = useMemo(() => !!getCampusConfig()?.is_cloud, [campusId]);
  const isGrandstream = useMemo(() => !!getCampusConfig()?.is_grandstream, [campusId]);
  const firstLoad = useRef(true);

  // ---------- Tab ----------
  const activeTab = initialTab;

  // ---------- Page-level Toast ----------
  const { toast, showToast, dismissToast } = useToast();

  // ---------- SIP registration in-flight ----------
  // Used to disable the bulk Register button + change its label while the
  // 4-7 second register_user_sip transaction runs. The single-row kebab path
  // shares the same flag for parity; the persistent info toast is the
  // primary visual feedback in both cases.
  const [isRegisteringSip, setIsRegisteringSip] = useState(false);

  // ==========================================================================
  // USERS TAB STATE
  // ==========================================================================
  const [userSearch, setUserSearch] = useState('');
  const [userFilters, setUserFilters] = useState({});
  const [userSortColumn, setUserSortColumn] = useState('username');
  const [userSortOrder, setUserSortOrder] = useState('asc');
  const [userPage, setUserPage] = useState(1);
  const [userPerPage, setUserPerPage] = usePersistedPerPage();
  // forceInclude surfaces the "Last Logged In (Mobile)" column ONCE for
  // existing users with a saved column preference, then records a "shown"
  // marker so they can still toggle it off. New users get it by default.
  const [userVisibleColumns, handleUserColumnToggle] = usePersistedColumns('users', DEFAULT_USER_VISIBLE, ALL_USER_KEYS, ['last_logged_in']);
  const [userSelectedIds, setUserSelectedIds] = useState([]);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  /* Typed confirmations for the destructive paths — same pattern as
     ResidentsPage / AreasPage. The row state doubles as the open flag. */
  const [deleteUserRow, setDeleteUserRow] = useState(null);
  const [deleteUserSaving, setDeleteUserSaving] = useState(false);
  const [bulkDeleteUsersOpen, setBulkDeleteUsersOpen] = useState(false);
  const [bulkDeleteUsersSaving, setBulkDeleteUsersSaving] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  // ==========================================================================
  // USER GROUPS TAB STATE
  // ==========================================================================
  const [groupSearch, setGroupSearch] = useState('');
  const [groupFilters, setGroupFilters] = useState({});
  const [groupSortColumn, setGroupSortColumn] = useState('group_name');
  const [groupSortOrder, setGroupSortOrder] = useState('asc');
  const [groupPage, setGroupPage] = useState(1);
  const [groupPerPage, setGroupPerPage] = usePersistedPerPage();
  const [groupVisibleColumns, handleGroupColumnToggle] = usePersistedColumns('user-groups', DEFAULT_GROUP_VISIBLE, ALL_GROUP_KEYS);
  const [groupSelectedIds, setGroupSelectedIds] = useState([]);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState(null);   // single group or { bulk: true, count }
  const [deletingGroup, setDeletingGroup] = useState(false);

  // ---------- Helper: get session params for API write calls ----------
  const getSessionParams = useCallback(() => {
    const session = getSession();
    return {
      campusId,
      userId: getUserId(),
      clientId: getSessionId(),
      username: session?.username || '',
    };
  }, [campusId]);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================

  /** Load users + roles + group list (for user add/edit form) */
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const isFirstLoad = firstLoad.current;
      const [uData, rData, gData, sData] = await Promise.all([
        fetchUsers(campusId, !isFirstLoad),
        fetchRoles(),
        fetchUserGroupList(campusId),
        fetchSchedules(campusId),
      ]);
      firstLoad.current = false;
      setUsersData(uData || []);
      setRoleData(rData || []);
      setGroupListData(gData || []);
      setSchedulesData(Array.isArray(sData) ? sData : []);
    } catch (err) {
      console.error('[UsersPage] Failed to load users data:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [campusId]);

  /** Load user groups (with their user members) */
  const loadUserGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const data = await fetchUserGroups(campusId);
      setUserGroupsData(data || []);
    } catch (err) {
      console.error('[UsersPage] Failed to load user groups:', err);
    } finally {
      setGroupsLoading(false);
    }
  }, [campusId]);

  // Initial load
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadUserGroups(); }, [loadUserGroups]);

  // ==========================================================================
  // USERS TAB — Filter / Search / Sort pipeline
  // ==========================================================================

  // Normalize the "last logged in" timestamp across campus types BEFORE the
  // sort/filter pipeline so the column can read a single field uniformly:
  //   - Legacy (non-cloud): backend already returns `last_logged_in`.
  //   - Cloud, user currently online: backend leaves `last_logged_in` empty
  //     and exposes the active session's start time in `most_recent_login`.
  //   - Cloud, user offline: backend may use either field.
  // Coalescing here keeps the "Last Login (Mobile)" column accurate AND lets
  // its sort work because `row.last_logged_in` is no longer null for
  // currently-online cloud users.
  const enrichedUsers = useMemo(() => {
    return (usersData || []).map((u) => {
      if (u.last_logged_in) return u;
      const fallback = u.most_recent_login || u.last_login_time || '';
      if (!fallback) return u;
      return { ...u, last_logged_in: fallback };
    });
  }, [usersData]);

  const filteredUsers = useMemo(() => {
    let data = [...enrichedUsers];

    // 1. Search
    if (userSearch.trim()) {
      const s = userSearch.toLowerCase();
      data = data.filter(
        (r) =>
          r.username?.toLowerCase().includes(s) ||
          r.display_name?.toLowerCase().includes(s) ||
          r.email?.toLowerCase().includes(s) ||
          r.user_role_name?.toLowerCase().includes(s) ||
          r.title?.toLowerCase().includes(s) ||
          r.phone?.toLowerCase().includes(s) ||
          r.group_name?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(userFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;

      data = data.filter((row) => {
        if (columnKey === 'group_name') {
          const gName = row.group_name || '';
          if (values.includes('(empty)') && !gName) return true;
          // group_name is comma-separated
          const names = gName.split(',').map((n) => n.trim()).filter(Boolean);
          return names.some((n) => values.includes(n)) || (values.includes('(empty)') && names.length === 0);
        }
        if (columnKey === 'reports') {
          // Build report names for this user from schedulesData
          const uid = String(row.user_id);
          const reportNames = (schedulesData || [])
            .filter((s) => (s.report_user_ids || '').split(',').map((id) => id.trim()).includes(uid))
            .map((s) => s.description || '(untitled)');
          if (values.includes('(empty)') && reportNames.length === 0) return true;
          return reportNames.some((n) => values.includes(n));
        }
        if (columnKey === 'is_logged_in') {
          const label = row.is_logged_in ? 'Online' : 'Offline';
          return values.includes(label);
        }
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal = a[userSortColumn];
      let bVal = b[userSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return userSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [enrichedUsers, userSearch, userFilters, userSortColumn, userSortOrder, schedulesData]);

  // Users Pagination
  const userTotalFiltered = filteredUsers.length;
  const userTotalPages = Math.max(1, Math.ceil(userTotalFiltered / userPerPage));
  const userSafePage = Math.min(userPage, userTotalPages);

  const paginatedUsers = useMemo(() => {
    const start = (userSafePage - 1) * userPerPage;
    return filteredUsers.slice(start, start + userPerPage);
  }, [filteredUsers, userSafePage, userPerPage]);


  useEffect(() => { setUserPage(1); }, [userSearch, userFilters, userPerPage]);


  // Users handlers
  const handleUserSort = useCallback((column) => {
    if (userSortColumn === column) {
      setUserSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setUserSortColumn(column);
      setUserSortOrder('asc');
    }
  }, [userSortColumn]);

  const handleUserFilterChange = useCallback((columnKey, values) => {
    setUserFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleUserRemoveFilter = useCallback((columnKey, value) => {
    setUserFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleUserClearAllFilters = useCallback(() => {
    setUserFilters({});
    setUserSearch('');
  }, []);

  const handleUserSelectRow = useCallback((id) => {
    setUserSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  /** Select-all respects role_weight: only selects users the current user has authority over */
  const handleUserSelectAll = useCallback(() => {
    const manageableIds = paginatedUsers
      .filter((u) => iAmHigherAuth(u.user_role_weight, myRoleWeight))
      .map((u) => u.user_id);
    const allSelected = manageableIds.length > 0 && manageableIds.every((id) => userSelectedIds.includes(id));
    if (allSelected) {
      setUserSelectedIds((prev) => prev.filter((id) => !manageableIds.includes(id)));
    } else {
      setUserSelectedIds((prev) => [...new Set([...prev, ...manageableIds])]);
    }
  }, [paginatedUsers, userSelectedIds, myRoleWeight]);

  // Administrators (role_weight 20) may add users at their own level on add only.
  const canAddUser = useMemo(
    () => (roleData || []).some((r) =>
      iAmHigherAuth(r.user_role_weight, myRoleWeight) ||
      (myRoleWeight === 20 && r.user_role_weight === 20)
    ),
    [roleData, myRoleWeight]
  );

  // Group management (Add/Delete/edit name/capcode) restricted to role_weight > 80.
  // Lower roles can still open the drawer to manage the assignable user list.
  const canManageGroups = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  /* CSV user upload — same role_weight > 80 bar as group management. Note
     iAmHigherAuth is strictly greater (or >= 1000), so weight 80 itself does not
     qualify; this is the same call the rest of the page makes, so the upload can
     never be reachable at a weight the page treats as lower elsewhere.

     The per-ROW role check in validateUserCsv is separate and still required: this
     only decides who sees the button, not which roles they may create. */
  const canUploadUsers = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);
  const [uploadOpen, setUploadOpen] = useState(false);

  /* Existing usernames, for the in-file collision check. Lower-cased comparison
     happens inside the validator. */
  const existingUsernames = useMemo(
    () => (usersData || []).map((u) => u.username).filter(Boolean),
    [usersData],
  );

  const openUploadUsers = useCallback(() => {
    if (!canUploadUsers) {
      showToast('error', 'You do not have permission to upload users.');
      return;
    }
    setUploadOpen(true);
  }, [canUploadUsers, showToast]);

  /**
   * Submit validated CSV rows.
   *
   * Sequential requests of UPLOAD_CHUNK_SIZE rows (10 today, measured against a
   * 30-row upload taking 15-20s). Changing that constant is the only edit needed
   * to resize or to go back to one request for everything — uploadInChunks walks
   * whatever chunkPayloads gives it, and addUsers takes a list either way.
   *
   * Because it is chunked, a failure part-way through means earlier chunks HAVE
   * been created; `result.sent` is how many, and the error toast says so.
   */
  const handleUploadUsers = useCallback(async (payloads, onProgress) => {
    const session = getSessionParams();
    const result = await uploadInChunks(payloads, {
      chunkSize: UPLOAD_CHUNK_SIZE,
      onProgress,
      submit: (chunk) => addUsers(
        /* rowNum is preflight bookkeeping, not part of the payload. */
        chunk.map(({ rowNum, ...row }) => ({ ...row, is_grandstream: isGrandstream })),
        session,
      ),
    });

    if (result.ok) {
      setUploadOpen(false);
      loadUsers();
      loadUserGroups();
      showToast('success', `${result.sent} ${result.sent === 1 ? 'user' : 'users'} uploaded.`);
      return;
    }

    /* Says how many rows did land before the failure, so a partial import is
       recoverable rather than a guess. With a single request `sent` is 0 and the
       whole batch was rejected. */
    const landed = result.sent > 0
      ? ` ${result.sent} of ${result.total} were created before it stopped.`
      : ' No users were created.';
    showToast('error', `Upload failed: ${result.error}${landed}`, true);
  }, [getSessionParams, isGrandstream, loadUsers, loadUserGroups, showToast]);

  // User drawer handlers
  const openAddUser = useCallback(() => {
    if (!canAddUser) {
      showToast('error', 'You do not have permission to add users.');
      return;
    }
    setEditingUser(null);
    setUserDrawerOpen(true);
  }, [canAddUser, showToast]);

  const openEditUser = useCallback((user) => {
    // Block editing users with equal/higher weight (unless PalCare Admin or self)
    if (!iAmHigherAuth(user.user_role_weight, myRoleWeight) && user.username !== myUsername) {
      showToast('error', `You do not have permission to edit ${user.display_name || user.username}.`);
      return;
    }
    setEditingUser(user);
    setUserDrawerOpen(true);
  }, [myRoleWeight, myUsername, showToast]);

  const handleSaveUser = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      if (formData.user_id) {
        // Edit mode — build oldData from current users array.
        // sip_extension / wave_password ride along from the existing user
        // record; is_grandstream comes from campus config (same source as add).
        const original = usersData.find((u) => u.user_id === formData.user_id);
        const enrichedFormData = {
          ...formData,
          sip_extension: original?.sip_extension,
          wave_password: original?.wave_password,
          is_grandstream: isGrandstream,
        };
        const oldData = {
          user_id: original?.user_id,
          display_name: original?.display_name,
          email: original?.email,
          user_role_id: original?.user_role_id,
          title: original?.title,
          group_ids: (original?.group_ids || []).map((g) =>
            (typeof g === 'object' && g !== null ? g.group_id : g)?.toString() || ''
          ).filter(Boolean),
          username: original?.username,
          cognito_id: original?.cognito_id,
          phone: original?.phone,
          sip_extension: original?.sip_extension,
          wave_password: original?.wave_password,
          is_grandstream: isGrandstream,
        };
        result = await editUser(enrichedFormData, oldData, session);

        // If editing own profile, update session storage
        if (result[0] && original?.username === myUsername) {
          const s = getSession();
          if (s) {
            s.email = formData.email;
            s.display = formData.display_name;
            s.groups = formData.group_ids;
            setSession(s);
          }
        }
      } else {
        // Add mode — tag with the campus's grandstream flag so the backend
        // can decide whether to provision a SIP extension server-side.
        const addPayload = { ...formData, is_grandstream: isGrandstream };
        result = await addUser(addPayload, session);
      }

      if (result[0]) {
        setUserDrawerOpen(false);
        loadUsers();
        loadUserGroups(); // Users display group names
        showToast('success', formData.user_id
          ? `${formData.display_name} updated`
          : `${formData.display_name || formData.username} added`
        );
      } else {
        showToast('error', result[1] || 'Failed to save user');
      }
    },
    [usersData, getSessionParams, loadUsers, loadUserGroups, showToast, myUsername, isGrandstream]
  );

  /* Entry point for the table kebab and the user drawer's Delete button. The
     authority check stays here so a refused delete answers with a toast rather
     than opening a dialog that cannot succeed. */
  const handleDeleteUser = useCallback(
    (user) => {
      if (!user) return;
      const userName = user.display_name || user.username;
      if (!iAmHigherAuth(user.user_role_weight, myRoleWeight)) {
        showToast('error', `You do not have permission to delete ${userName}.`);
        return;
      }
      setDeleteUserRow(user);
    },
    [myRoleWeight, showToast]
  );

  const handleConfirmDeleteUser = useCallback(async () => {
    const user = deleteUserRow;
    if (!user) return;
    const userName = user.display_name || user.username;
    /* Re-check on confirm: the dialog can outlive a refresh that changed this
       user's role, and deleting a peer or senior must never slip through. */
    if (!iAmHigherAuth(user.user_role_weight, myRoleWeight)) {
      setDeleteUserRow(null);
      showToast('error', `You do not have permission to delete ${userName}.`);
      return;
    }
    setDeleteUserSaving(true);
    const session = getSessionParams();
    const result = await deleteUsers([{
      user_id: user.user_id,
      username: user.username,
      cognito_id: user.cognito_id,
      user_role_id: user.user_role_id,
      sip_extension: user.sip_extension,
    }], session);
    setDeleteUserSaving(false);

    if (result[0]) {
      setDeleteUserRow(null);
      setUserSelectedIds((prev) => prev.filter((id) => id !== user.user_id));
      setUserDrawerOpen(false);
      loadUsers();
      loadUserGroups();
      showToast('success', `${userName} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [deleteUserRow, myRoleWeight, getSessionParams, loadUsers, loadUserGroups, showToast]);

  /* Split the selection by authority. Users at or above the caller's own role
     are skipped rather than failing the batch, so the dialog names them — the
     old flow fired a toast *before* the native confirm, which was easy to miss
     and gone by the time you were deciding. Shared by the dialog body and the
     confirm handler so the two can never disagree. */
  const bulkUserDeletePlan = useMemo(() => {
    const manageable = [];
    const denied = [];
    userSelectedIds.forEach((id) => {
      const user = usersData.find((u) => u.user_id === id);
      if (!user) return;
      if (iAmHigherAuth(user.user_role_weight, myRoleWeight)) manageable.push(user);
      else denied.push(user);
    });
    return {
      manageable,
      denied,
      deniedNames: denied.map((u) => u.display_name || u.username).join(', '),
    };
  }, [userSelectedIds, usersData, myRoleWeight]);

  const handleBulkDeleteUsers = useCallback(() => {
    if (bulkUserDeletePlan.manageable.length === 0) {
      showToast('error', 'You do not have permission to delete any of the selected users.');
      return;
    }
    setBulkDeleteUsersOpen(true);
  }, [bulkUserDeletePlan, showToast]);

  const handleConfirmBulkDeleteUsers = useCallback(async () => {
    const { manageable } = bulkUserDeletePlan;
    /* The plan recomputes from live data, so a refresh can strip the last
       manageable user while the dialog is open. Say so instead of no-opping. */
    if (manageable.length === 0) {
      setBulkDeleteUsersOpen(false);
      showToast('error', 'You do not have permission to delete any of the selected users.');
      return;
    }
    setBulkDeleteUsersSaving(true);
    const session = getSessionParams();
    const count = manageable.length;

    const payload = manageable.map((u) => ({
      user_id: u.user_id,
      username: u.username,
      cognito_id: u.cognito_id,
      user_role_id: u.user_role_id,
      sip_extension: u.sip_extension
    }));

    const result = await deleteUsers(payload, session);
    setBulkDeleteUsersSaving(false);
    if (result[0]) {
      setBulkDeleteUsersOpen(false);
      setUserSelectedIds([]);
      loadUsers();
      loadUserGroups();
      showToast('success', `${count} user${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [bulkUserDeletePlan, getSessionParams, loadUsers, loadUserGroups, showToast]);

  // Opens the Reset Password dialog from row action or drawer
  const openResetPasswordDialog = useCallback((user) => {
    setResetPasswordUser(user);
  }, []);

  // Called by ResetPasswordDialog after the user enters a new password
  const handleResetPassword = useCallback(
    async (data) => {
      const session = getSessionParams();
      const result = await resetPassword(data, session);
      if (result[0]) {
        setResetPasswordUser(null);
        showToast('success', `Password reset for ${data.username}`);
      }
      return result;
    },
    [getSessionParams, showToast]
  );

  // ----- SIP Extension registration (grandstream campuses only) -----

  /** Single-row "Register SIP Extension" from the kebab menu.
      In-flight visual feedback is handled by the kebab item itself (which
      shows an inline spinner while awaiting this Promise) — so no info
      toast here, just the success/error result. */
  const handleRegisterUserSip = useCallback(async (user) => {
    if (!user?.user_id) return;
    const session = getSessionParams();
    const label = user.display_name || user.username || 'user';
    const result = await registerUserSip([user.user_id], session);
    if (result[0]) {
      loadUsers();
      showToast('success', `SIP extension registered for ${label}`);
    } else {
      showToast('error', result[1] || 'Failed to register SIP extension');
    }
  }, [getSessionParams, loadUsers, showToast]);

  /** Bulk "Register SIP Extensions" from the action bar — only registers
      selected users that don't already have a SIP extension. */
  const handleBulkRegisterUserSip = useCallback(async () => {
    const candidates = usersData.filter((u) =>
      userSelectedIds.includes(u.user_id)
      && (u.sip_extension == null || Number(u.sip_extension) === 0)
    );
    if (candidates.length === 0) {
      showToast('error', 'None of the selected users need a SIP extension.');
      return;
    }
    const session = getSessionParams();
    const n = candidates.length;
    // The bulk button shows its own inline spinner via isRegisteringSip —
    // no need for an additional in-flight toast.
    setIsRegisteringSip(true);
    try {
      const result = await registerUserSip(candidates.map((u) => u.user_id), session);
      if (result[0]) {
        setUserSelectedIds([]);
        loadUsers();
        showToast('success', `${n} SIP extension${n === 1 ? '' : 's'} registered`);
      } else {
        showToast('error', result[1] || 'Failed to register SIP extensions');
      }
    } finally {
      setIsRegisteringSip(false);
    }
  }, [usersData, userSelectedIds, getSessionParams, loadUsers, showToast]);

  // User exports
  const handleUserExportAll = useCallback(() => {
    if (filteredUsers.length) exportUsersCsv(filteredUsers, 'users.csv', myRoleWeight, userVisibleColumns);
  }, [filteredUsers, myRoleWeight, userVisibleColumns]);

  const handleUserBulkExport = useCallback(() => {
    const selected = usersData.filter((u) => userSelectedIds.includes(u.user_id));
    if (selected.length) exportUsersCsv(selected, 'users-selected.csv', myRoleWeight, userVisibleColumns);
  }, [userSelectedIds, usersData, myRoleWeight, userVisibleColumns]);

  // ==========================================================================
  // USER GROUPS TAB — Filter / Search / Sort pipeline
  // ==========================================================================
  /* Blast-radius counts for the delete confirmations. `group_name` on a user is
     a comma-separated string from the API (not an array) — parsed the same way
     UsersTable builds its filter values. */
  const deleteUserGroupCount = useMemo(() => {
    if (!deleteUserRow) return 0;
    const fresh = usersData.find((u) => u.user_id === deleteUserRow.user_id);
    const raw = (fresh || deleteUserRow).group_name;
    if (!raw || !raw.trim()) return 0;
    return raw.split(',').map((g) => g.trim()).filter(Boolean).length;
  }, [deleteUserRow, usersData]);

  /* Members of the group(s) queued for deletion, counted distinctly — one user
     can belong to several of the selected groups. Deleting a group does not
     delete its users (sp_deleteUserGroup only clears core_group,
     core_group_map and core_group_entity), which is what the copy says. */
  const deleteGroupUserCount = useMemo(() => {
    if (!deleteGroupConfirm) return 0;
    const ids = new Set();
    if (deleteGroupConfirm.bulk) {
      (userGroupsData || []).forEach((g) => {
        if (!groupSelectedIds.includes(g.group_id)) return;
        (g.users || []).forEach((u) => ids.add(u.user_id));
      });
    } else {
      const fresh = (userGroupsData || []).find((g) => g.group_id === deleteGroupConfirm.group_id);
      ((fresh || deleteGroupConfirm).users || []).forEach((u) => ids.add(u.user_id));
    }
    return ids.size;
  }, [deleteGroupConfirm, groupSelectedIds, userGroupsData]);

  const enrichedGroups = useMemo(() => {
    return (userGroupsData || []).map((group) => ({
      ...group,
      user_count: (group.users || []).length,
    }));
  }, [userGroupsData]);

  const filteredGroups = useMemo(() => {
    let data = [...enrichedGroups];

    // 1. Search
    if (groupSearch.trim()) {
      const s = groupSearch.toLowerCase();
      data = data.filter(
        (g) =>
          g.group_name?.toLowerCase().includes(s) ||
          g.capcode?.toLowerCase().includes(s) ||
          (g.users || []).some((u) => u.display_name?.toLowerCase().includes(s) || u.username?.toLowerCase().includes(s))
      );
    }

    // 2. Column filters
    Object.entries(groupFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal, bVal;
      if (groupSortColumn === 'user_count') {
        aVal = (a.users || []).length;
        bVal = (b.users || []).length;
        const result = aVal - bVal;
        return groupSortOrder === 'asc' ? result : -result;
      }
      aVal = a[groupSortColumn];
      bVal = b[groupSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return groupSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [enrichedGroups, groupSearch, groupFilters, groupSortColumn, groupSortOrder]);

  // Groups Pagination
  const groupTotalFiltered = filteredGroups.length;
  const groupTotalPages = Math.max(1, Math.ceil(groupTotalFiltered / groupPerPage));
  const groupSafePage = Math.min(groupPage, groupTotalPages);

  const paginatedGroups = useMemo(() => {
    const start = (groupSafePage - 1) * groupPerPage;
    return filteredGroups.slice(start, start + groupPerPage);
  }, [filteredGroups, groupSafePage, groupPerPage]);


  useEffect(() => { setGroupPage(1); }, [groupSearch, groupFilters, groupPerPage]);


  // Group handlers
  const handleGroupSort = useCallback((column) => {
    if (groupSortColumn === column) {
      setGroupSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setGroupSortColumn(column);
      setGroupSortOrder('asc');
    }
  }, [groupSortColumn]);

  const handleGroupFilterChange = useCallback((columnKey, values) => {
    setGroupFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleGroupRemoveFilter = useCallback((columnKey, value) => {
    setGroupFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleGroupClearAllFilters = useCallback(() => {
    setGroupFilters({});
    setGroupSearch('');
  }, []);

  const handleGroupSelectRow = useCallback((id) => {
    setGroupSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleGroupSelectAll = useCallback(() => {
    const allIds = paginatedGroups.map((g) => g.group_id);
    const allSelected = allIds.every((id) => groupSelectedIds.includes(id));
    if (allSelected) {
      setGroupSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setGroupSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedGroups, groupSelectedIds]);

  // Group drawer handlers
  const openAddGroup = useCallback(() => {
    setEditingGroup(null);
    setGroupDrawerOpen(true);
  }, []);

  const openEditGroup = useCallback((group) => {
    setEditingGroup(group);
    setGroupDrawerOpen(true);
  }, []);

  const handleSaveGroup = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      // Resolve cognito_ids from users data
      const cognitoIds = (formData.user_ids || []).map((uid) => {
        const u = usersData.find((usr) => usr.user_id === uid);
        return u?.cognito_id || '';
      }).filter(Boolean);

      if (formData.group_id) {
        // Edit mode
        const original = userGroupsData.find((g) => g.group_id === formData.group_id);
        const oldUserIds = (original?.users || []).map((u) => u.user_id);
        const newUserIds = formData.user_ids || [];

        // Build remove list (cognito_ids of users being removed)
        const removeList = oldUserIds
          .filter((id) => !newUserIds.includes(id))
          .map((uid) => {
            const u = usersData.find((usr) => usr.user_id === uid);
            return u?.cognito_id || '';
          })
          .filter(Boolean);

        const editData = {
          group_id: formData.group_id,
          group_name: formData.group_name,
          capcode: formData.capcode,
          user_ids: newUserIds,
          cognito_ids: cognitoIds,
          remove_list: removeList,
        };
        const oldData = {
          group_id: original?.group_id,
          group_name: original?.group_name,
          capcode: original?.capcode,
          user_ids: oldUserIds,
          campus_id: campusId,
        };
        result = await editUserGroup(editData, oldData, session);
      } else {
        // Add mode
        const addData = {
          group_name: formData.group_name,
          capcode: formData.capcode,
          user_ids: formData.user_ids || [],
          cognito_ids: cognitoIds,
        };
        result = await addUserGroup(addData, session);
      }

      if (result[0]) {
        setGroupDrawerOpen(false);
        loadUserGroups();
        loadUsers(); // Users display group names
        showToast('success', formData.group_id
          ? `${formData.group_name} updated`
          : `${formData.group_name} created`
        );
      } else {
        showToast('error', result[1] || 'Failed to save group');
      }
    },
    [usersData, userGroupsData, campusId, getSessionParams, loadUserGroups, loadUsers, showToast]
  );

  // Show confirm dialog for single group delete (kebab / drawer)
  const handleDeleteGroup = useCallback((group) => {
    setDeleteGroupConfirm(group);
  }, []);

  // Show confirm dialog for bulk group delete (action bar)
  const handleGroupBulkDelete = useCallback(() => {
    setDeleteGroupConfirm({ bulk: true, count: groupSelectedIds.length });
  }, [groupSelectedIds.length]);

  // Actual delete after confirm
  const executeDeleteGroup = useCallback(async () => {
    if (!deleteGroupConfirm) return;
    setDeletingGroup(true);

    const session = getSessionParams();

    if (deleteGroupConfirm.bulk) {
      // Bulk delete
      const count = groupSelectedIds.length;
      const payload = groupSelectedIds.map((gid) => {
        const group = userGroupsData.find((g) => g.group_id === gid);
        return {
          group_id: gid,
          user_ids: (group?.users || []).map((u) => u.user_id),
          cognito_ids: (group?.users || []).map((u) => u.cognito_id).filter(Boolean),
        };
      });

      const result = await deleteUserGroups(payload, session);
      if (result[0]) {
        setGroupSelectedIds([]);
        loadUserGroups();
        loadUsers();
        showToast('success', `${count} group${count > 1 ? 's' : ''} deleted`);
      } else {
        showToast('error', result[1] || 'Bulk delete failed');
      }
    } else {
      // Single group delete
      const group = deleteGroupConfirm;
      const groupName = group.group_name;
      const userIds = (group.users || []).map((u) => u.user_id);
      const cognitoIds = (group.users || []).map((u) => u.cognito_id).filter(Boolean);

      const result = await deleteUserGroups([{
        group_id: group.group_id,
        user_ids: userIds,
        cognito_ids: cognitoIds,
      }], session);

      if (result[0]) {
        setGroupSelectedIds((prev) => prev.filter((id) => id !== group.group_id));
        setGroupDrawerOpen(false);
        loadUserGroups();
        loadUsers();
        showToast('success', `${groupName} deleted`);
      } else {
        showToast('error', result[1] || 'Delete failed');
      }
    }

    setDeletingGroup(false);
    setDeleteGroupConfirm(null);
  }, [deleteGroupConfirm, groupSelectedIds, userGroupsData, getSessionParams, loadUserGroups, loadUsers, showToast]);

  // Group exports
  const handleGroupExportAll = useCallback(() => {
    if (filteredGroups.length) exportUserGroupsCsv(filteredGroups, 'user-groups.csv', myRoleWeight, groupVisibleColumns);
  }, [filteredGroups, myRoleWeight, groupVisibleColumns]);

  const handleGroupBulkExport = useCallback(() => {
    const selected = enrichedGroups.filter((g) => groupSelectedIds.includes(g.group_id));
    if (selected.length) exportUserGroupsCsv(selected, 'user-groups-selected.csv', myRoleWeight, groupVisibleColumns);
  }, [groupSelectedIds, enrichedGroups, myRoleWeight, groupVisibleColumns]);

  // ==========================================================================
  // COLUMN LABEL MAPS (for ActiveFilterBar)
  // ==========================================================================
  // SIP Extension column is only meaningful on grandstream campuses — filter it
  // out of the visible column defs / toggle when the campus isn't grandstream.
  const userColumnDefs = useMemo(
    () => isGrandstream ? COLUMN_DEFS : COLUMN_DEFS.filter((c) => c.key !== 'sip_extension'),
    [isGrandstream]
  );
  const effectiveUserVisibleColumns = useMemo(
    () => isGrandstream ? userVisibleColumns : userVisibleColumns.filter((k) => k !== 'sip_extension'),
    [isGrandstream, userVisibleColumns]
  );
  const userColumnLabels = useMemo(
    () => Object.fromEntries(COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );
  const groupColumnLabels = useMemo(
    () => Object.fromEntries(GROUP_COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );

  // Campus name — derive from campusList (which correctly maps session.campusData names)
  const campusName = useMemo(() => {
    const match = campusList.find((c) => c.campus_id === campusId);
    return match?.campus_name || '';
  }, [campusId, campusList]);

  // ==========================================================================
  // Helper: Bulk action bar (reused across 2 tabs)
  // ==========================================================================
  const renderBulkBar = (selectedCount, entityLabel, onExport, onDelete, onClear, canDelete = true, onRegisterSip = null) => {
    if (selectedCount === 0) return null;
    return (
      <div className="up-bulk-bar">
        <div className="up-bulk-bar__left">
          <span className="up-bulk-bar__count">
            {selectedCount} {entityLabel}{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={onClear} className="up-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="up-bulk-bar__right">
          {onRegisterSip ? (
            <button
              type="button"
              onClick={onRegisterSip}
              disabled={isRegisteringSip}
              className="up-bulk-btn"
              aria-label={`Register SIP extensions for selected ${entityLabel}s`}
              aria-busy={isRegisteringSip}
            >
              {isRegisteringSip ? (
                <svg className="up-icon-sm up-toast__spinner" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M2.985 19.644v-4.992h4.992m-4.992 0a8.25 8.25 0 0014.142 2.515M21.015 9.348a8.25 8.25 0 00-14.142-2.515L2.985 10.86" />
                </svg>
              ) : (
                <svg className="up-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
              )}
              {isRegisteringSip ? 'Registering…' : 'Register SIP Extensions'}
            </button>
          ) : null}
          <button type="button" onClick={onExport} className="up-bulk-btn" aria-label={`Export selected ${entityLabel}s`}>
            <svg className="up-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          {canDelete ? (
            <button type="button" onClick={onDelete} className="up-bulk-btn up-bulk-btn--danger" aria-label={`Delete selected ${entityLabel}s`}>
              <svg className="up-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="ps-page">
      {/* Page-level toast notification */}
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="ps-page-header">
        <div className="ps-page-header__inner">
          <div className="ps-breadcrumb-row">
            <div className="ps-breadcrumb">
              <span className="ps-breadcrumb__text">Management</span>
              <svg className="up-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="ps-breadcrumb__current">Users</span>
            </div>
            <div className="ps-campus-row">
              <label htmlFor="up-campus-selector" className="ps-campus-label">Campus:</label>
              {campusList.length > 1 ? (
                <select
                  id="up-campus-selector"
                  value={campusId}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setCampusId(val);
                    updateSessionCampus(val);
                  }}
                  className="ps-campus-select"
                >
                  {campusList.map((c) => (
                    <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                      {c.campus_name || c.CampusName}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ps-campus-name">{campusName}</span>
              )}
            </div>
          </div>
          <h1 className="ps-page-title">Users</h1>
        </div>
      </div>

      {/* ============================================================
          TAB BAR
          ============================================================ */}
      <div className="up-tab-bar">
        <div className="up-tab-bar__inner">
          <nav className="up-tab-nav" aria-label="Tabs">
            <Link to="/users/usermanagement" className={`up-tab ${activeTab === 'users' ? 'is-active' : ''}`}>
              Users ({usersData.length})
              {activeTab === 'users' ? <span className="up-tab__indicator" /> : null}
            </Link>
            <Link to="/users/groups" className={`up-tab ${activeTab === 'usergroups' ? 'is-active' : ''}`}>
              User Groups ({enrichedGroups.length})
              {activeTab === 'usergroups' ? <span className="up-tab__indicator" /> : null}
            </Link>
          </nav>
        </div>
      </div>

      {/* ============================================================
          MAIN CONTENT
          ============================================================ */}
      <div className="up-content">
        {/* ========== USERS TAB ========== */}
        {activeTab === 'users' ? (
          <>
            <div className="up-controls">
              <div className="up-controls__left">
                <div className="up-search">
                  <svg className="up-icon-md up-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="up-search__input"
                    aria-label="Search users"
                  />
                </div>
              </div>
              <div className="up-controls__right">
                <ColumnVisibilityToggle columns={userColumnDefs} visibleColumns={effectiveUserVisibleColumns} onToggle={handleUserColumnToggle} />
                <button type="button" className="up-btn up-btn--secondary" onClick={handleUserExportAll} aria-label="Export users">
                  <svg className="up-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {canUploadUsers ? (
                  <button
                    type="button"
                    className="up-btn up-btn--secondary"
                    onClick={openUploadUsers}
                    aria-label="Upload users from CSV"
                  >
                    <svg className="up-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 7.5L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                    Upload
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={openAddUser}
                  disabled={!canAddUser}
                  title={canAddUser ? undefined : 'Your role is not permitted to add users.'}
                  className="up-btn up-btn--primary"
                  aria-label="Add new user"
                >
                  <svg className="up-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  New User
                </button>
              </div>
            </div>
            <ActiveFilterBar filters={userFilters} columnLabels={userColumnLabels} onRemoveFilter={handleUserRemoveFilter} onClearAll={handleUserClearAllFilters} />
            {renderBulkBar(
              userSelectedIds.length,
              'user',
              handleUserBulkExport,
              handleBulkDeleteUsers,
              () => setUserSelectedIds([]),
              true,
              isGrandstream ? handleBulkRegisterUserSip : null,
            )}
            {usersLoading ? (
              <TableSkeleton />
            ) : (
              <UsersTable
                data={paginatedUsers}
                allData={usersData}
                visibleColumns={effectiveUserVisibleColumns}
                sortColumn={userSortColumn}
                sortOrder={userSortOrder}
                onSort={handleUserSort}
                filters={userFilters}
                onFilterChange={handleUserFilterChange}
                selectedIds={userSelectedIds}
                onSelectRow={handleUserSelectRow}
                onSelectAll={handleUserSelectAll}
                onEditUser={openEditUser}
                onDeleteUser={handleDeleteUser}
                onResetPassword={openResetPasswordDialog}
                onRegisterSip={isGrandstream ? handleRegisterUserSip : undefined}
                onAddUser={openAddUser}
                onClearFilters={handleUserClearAllFilters}
                myRoleWeight={myRoleWeight}
                myUsername={myUsername}
                iAmHigherAuthority={iAmHigherAuth}
                roleData={roleData}
                isCloud={isCloud}
                isGrandstream={isGrandstream}
                schedulesData={schedulesData}
              />
            )}
            {!usersLoading && userTotalFiltered > 0
              ? <Pagination page={userSafePage} totalPages={userTotalPages} total={userTotalFiltered} perPage={userPerPage} onPageChange={setUserPage} onPerPageChange={setUserPerPage} entityLabel="users" selectId="user-per-page" />
              : null}
          </>
        ) : (
          /* ========== USER GROUPS TAB ========== */
          <>
            <div className="up-controls">
              <div className="up-controls__left">
                <div className="up-search">
                  <svg className="up-icon-md up-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="up-search__input"
                    aria-label="Search user groups"
                  />
                </div>
              </div>
              <div className="up-controls__right">
                <ColumnVisibilityToggle columns={GROUP_COLUMN_DEFS} visibleColumns={groupVisibleColumns} onToggle={handleGroupColumnToggle} />
                <button type="button" className="up-btn up-btn--secondary" onClick={handleGroupExportAll} aria-label="Export groups">
                  <svg className="up-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {canManageGroups ? (
                  <button type="button" onClick={openAddGroup} className="up-btn up-btn--primary" aria-label="Add new group">
                    <svg className="up-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    New Group
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={groupFilters} columnLabels={groupColumnLabels} onRemoveFilter={handleGroupRemoveFilter} onClearAll={handleGroupClearAllFilters} />
            {renderBulkBar(groupSelectedIds.length, 'group', handleGroupBulkExport, handleGroupBulkDelete, () => setGroupSelectedIds([]), canManageGroups)}
            {groupsLoading ? (
              <TableSkeleton />
            ) : (
              <UserGroupsTable
                data={paginatedGroups}
                allData={enrichedGroups}
                visibleColumns={groupVisibleColumns}
                sortColumn={groupSortColumn}
                sortOrder={groupSortOrder}
                onSort={handleGroupSort}
                filters={groupFilters}
                onFilterChange={handleGroupFilterChange}
                selectedIds={groupSelectedIds}
                onSelectRow={handleGroupSelectRow}
                onSelectAll={handleGroupSelectAll}
                onEditGroup={openEditGroup}
                onDeleteGroup={handleDeleteGroup}
                onAddGroup={openAddGroup}
                onClearFilters={handleGroupClearAllFilters}
                canManageGroups={canManageGroups}
              />
            )}
            {!groupsLoading && groupTotalFiltered > 0
              ? <Pagination page={groupSafePage} totalPages={groupTotalPages} total={groupTotalFiltered} perPage={groupPerPage} onPageChange={setGroupPage} onPerPageChange={setGroupPerPage} entityLabel="groups" selectId="group-per-page" />
              : null}
          </>
        )}
      </div>

      {/* ============================================================
          DRAWERS
          ============================================================ */}
      <UserDrawer
        isOpen={userDrawerOpen}
        onClose={() => setUserDrawerOpen(false)}
        user={editingUser}
        onSave={handleSaveUser}
        onDelete={handleDeleteUser}
        onResetPassword={handleResetPassword}
        roles={roleData}
        groups={groupListData}
        schedules={schedulesData}
        campusId={campusId}
        myRoleWeight={myRoleWeight}
        myUsername={myUsername}
        displayEditAllowed={displayEditAllowed}
        iAmHigherAuthority={iAmHigherAuth}
        isCorp={isCorp}
      />

      <UserGroupDrawer
        isOpen={groupDrawerOpen}
        onClose={() => setGroupDrawerOpen(false)}
        group={editingGroup}
        onSave={handleSaveGroup}
        onDelete={handleDeleteGroup}
        users={usersData}
        myRoleWeight={myRoleWeight}
        iAmHigherAuthority={iAmHigherAuth}
        canManageGroups={canManageGroups}
      />

      {/* CSV user upload. Roles are passed unfiltered: the validator applies the
          same per-role authority check the drawer's dropdown does, and needs the
          full list to tell "role not available at this campus" apart from "your
          role cannot create that". */}
      <UserUploadDialog
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUploadUsers}
        roles={roleData}
        myRoleWeight={myRoleWeight}
        canAssignWeight={iAmHigherAuth}
        existingUsernames={existingUsernames}
      />

      {/* Reset Password Dialog */}
      {resetPasswordUser ? (
        <ResetPasswordDialog
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onConfirm={handleResetPassword}
          roleData={roleData}
        />
      ) : null}

      {/* ============================================================
          DESTRUCTIVE CONFIRMATIONS
          ============================================================
          All four require typing "confirm". The single-entity dialogs are
          `elevated` because they can be opened from inside a drawer, which sits
          above the default dialog backdrop. */}
      {deleteUserRow ? (
        <ConfirmTypedDialog
          title="Delete User"
          confirmLabel="Delete"
          savingLabel="Deleting…"
          elevated
          onClose={() => setDeleteUserRow(null)}
          onConfirm={handleConfirmDeleteUser}
          saving={deleteUserSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong>{deleteUserRow.display_name || deleteUserRow.username}</strong>?
          </p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Their sign-in account will be removed and they will immediately lose access to the web portal and mobile
            {deleteUserGroupCount > 0 ? (
              <>
                . They will also be removed from{' '}
                <strong>
                  {deleteUserGroupCount} group{deleteUserGroupCount > 1 ? 's' : ''}
                </strong>
              </>
            ) : null}
            . <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {bulkDeleteUsersOpen ? (
        <ConfirmTypedDialog
          title={`Delete ${bulkUserDeletePlan.manageable.length} User${bulkUserDeletePlan.manageable.length > 1 ? 's' : ''}`}
          confirmLabel="Delete"
          savingLabel="Deleting…"
          onClose={() => setBulkDeleteUsersOpen(false)}
          onConfirm={handleConfirmBulkDeleteUsers}
          saving={bulkDeleteUsersSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong>
              {bulkUserDeletePlan.manageable.length} user
              {bulkUserDeletePlan.manageable.length > 1 ? 's' : ''}
            </strong>
            ?
          </p>
          {bulkUserDeletePlan.denied.length > 0 ? (
            <p style={{ margin: 'var(--space-2) 0 0', lineHeight: 1.6 }}>
              <strong>
                {bulkUserDeletePlan.denied.length} selected user
                {bulkUserDeletePlan.denied.length > 1 ? 's' : ''}
              </strong>{' '}
              will be skipped — you do not have permission to delete{' '}
              {bulkUserDeletePlan.deniedNames}.
            </p>
          ) : null}
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Their sign-in accounts will be removed and they will immediately lose access to the web portal and mobile.{' '}
            <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {/* Single and bulk group delete share one dialog, keyed off the union
          state `deleteGroupConfirm` (a group object, or { bulk, count }). */}
      {deleteGroupConfirm ? (
        <ConfirmTypedDialog
          title={
            deleteGroupConfirm.bulk
              ? `Delete ${deleteGroupConfirm.count} Group${deleteGroupConfirm.count > 1 ? 's' : ''}`
              : 'Delete Group'
          }
          confirmLabel="Delete"
          savingLabel="Deleting…"
          elevated={!deleteGroupConfirm.bulk}
          onClose={() => setDeleteGroupConfirm(null)}
          onConfirm={executeDeleteGroup}
          saving={deletingGroup}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            {deleteGroupConfirm.bulk ? (
              <strong>
                {deleteGroupConfirm.count} group{deleteGroupConfirm.count > 1 ? 's' : ''}
              </strong>
            ) : (
              <strong>{deleteGroupConfirm.group_name}</strong>
            )}
            ?
          </p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {deleteGroupUserCount > 0 ? (
              <>
                {deleteGroupConfirm.bulk && deleteGroupConfirm.count > 1 ? 'These groups' : 'This group'} will be
                removed from{' '}
                <strong>
                  {deleteGroupUserCount} user{deleteGroupUserCount > 1 ? 's' : ''}
                </strong>
                .{' '}
              </>
            ) : null}
            The users themselves are not deleted. <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}
    </div>
    </div>
    </>
  );
};

export default UsersPage;
